<script setup lang="ts">
import { computed } from 'vue'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useChatStore } from '@/stores/chatStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import Backend from '@/models/backend.ts'
import MediumModel from '@/components/Interface/Modal/ModelSizes/MediumModel.vue'
import router from '@/router'
import { logger } from '@/models/logger'
import { useI18n } from 'vue-i18n'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'

const { t } = useI18n()

/**
 * Stores
 */
const playerRelationStore = usePlayerRelationStore()
const chatStore = useChatStore()
const gameStore = useGameStore()

/**
 * Computed
 */
const getPlayersWithRoom = computed(() => {
  // Get all players from the playerRelationStore
  const relations = playerRelationStore.relations

  // Map relations to players with room info and filter out existing chats
  return Array.from(relations.values())
    .map((relation) => {
      // Generate the room key
      const roomKey =
        'private_' +
        Math.min(relation.playerId, gameStore.player!.id) +
        '_' +
        Math.max(relation.playerId, gameStore.player!.id)

      // Check if this room exists
      if (chatStore.getRoomByRoomKey(roomKey) === undefined) {
        // Include this player since they don't have a chat room yet
        return {
          ...relation,
          roomId: roomKey
        }
      }
      // Don't include this player by returning null
      return null
    })
    .filter((player) => player !== null)
})

/**
 * Functions
 */
const createPrivateChatRoom = async (playerId: number) => {
  const backend = new Backend()
  try {
    const response = await backend.createPrivateChatRoom(playerId)
    gameStore.modalType = ''
  
    chatStore.setRoom(response.data.chatRoom)
    chatStore.setActiveRoomId(response.data.chatRoom.id)
    router.push(`/communication/${response.data.chatRoom.id}`)
  } catch (error) {
    logger.error('Error creating private chat room:', error)
  }
}
</script>

<template>
  <MediumModel :title="t('modals.newChat.title')">
    <div class="p-2">
      <div class="grid grid-cols-2 gap-2">
        <template v-for="player in getPlayersWithRoom" :key="player.playerId">
          <StandardCard top-padding="0">
            <div
              class="flex flex-col justify-center relative cursor-pointer"
              @click="createPrivateChatRoom(player!.playerId)"
            >
              <div
                class="text-metal-300 p-1 absolute bottom-0 w-full bg-black bg-opacity-50 text-center rounded-b z-10"
              >
                {{ player!.name }}
              </div>
              <div class="w-full aspect-square">
                <StyledImage :src="player!.profileImage" size="full" />
              </div>
            </div>
          </StandardCard>
        </template>
      </div>
      <StandardCard v-if="playerRelationStore.relations.size === 0" top-padding="0">
        <div>{{ t('modals.newChat.noEmpiresYet') }}</div>
      </StandardCard>
      <StandardCard v-else-if="getPlayersWithRoom.length === 0" top-padding="0">
        <div>{{ t('modals.newChat.allChatsOpen') }}</div>
      </StandardCard>
    </div>
  </MediumModel>
</template>

<style scoped lang="scss"></style>
